/**
 * High-level pointing orchestration: takes a `PointingTag`, resolves
 * it to a viewport target, drives the bird's bezier-arc flight via
 * `MovementController`, holds the pointing pose briefly, then returns
 * the bird to its normal follow behavior. Exists so the Claude pipeline
 * doesn't need to know about DOM resolution details.
 */

import type { PageContext, PointingTag } from "../types";
import type { MovementController } from "../sprite/MovementController";
import { resolvePointingTarget } from "./ElementLocator";

// Bumped from 3.0s → 3.5s so the red pointing callout has time to
// scale-in, sit visibly, and fade out without feeling rushed.
const POINTING_HOLD_SECONDS = 3.5;

export interface PointAtCallbacks {
  /**
   * Fires the moment the bezier flight resolves and the bird begins
   * holding the pointing pose. Used to pop the red arrival callout.
   */
  onArrival?: () => void;
  /**
   * Fires after `POINTING_HOLD_SECONDS` elapses. If provided, this
   * supersedes the default "return to cursor follow" behavior — the
   * caller is responsible for putting the bird somewhere (e.g. fly
   * back to the nest corner). Default: `movementController.returnToFollow()`.
   */
  onHoldComplete?: () => void;
}

export class FlightController {
  private readonly movementController: MovementController;
  private activePointingTimerId: number | null = null;
  private holdTrackerRafId: number | null = null;

  constructor(movementController: MovementController) {
    this.movementController = movementController;
  }

  async pointAt(
    tag: PointingTag,
    pageContext: PageContext,
    callbacks: PointAtCallbacks = {},
  ): Promise<void> {
    const target = await resolvePointingTarget(tag, pageContext);
    if (!target) return;

    // Belt-and-suspenders against the "ghost point" symptom: if the
    // resolved coordinates fall outside the visible viewport (with a
    // small margin), the layout has changed since the screenshot was
    // taken and the pixel-coord fallback is now meaningless. Better
    // to abort than fly the bird to an empty corner.
    const offscreenMarginPx = 50;
    if (
      target.viewportX < -offscreenMarginPx ||
      target.viewportX > window.innerWidth + offscreenMarginPx ||
      target.viewportY < -offscreenMarginPx ||
      target.viewportY > window.innerHeight + offscreenMarginPx
    ) {
      console.warn(
        `[koko] pointing target offscreen (${Math.round(target.viewportX)},${Math.round(target.viewportY)}) — aborting flight`,
      );
      return;
    }

    await this.movementController.flyToPointingTarget({
      viewportX: target.viewportX,
      viewportY: target.viewportY,
    });

    callbacks.onArrival?.();
    this.startHoldTracker(target.refreshPosition);
    // Resolve only after the hold completes so the calling pipeline
    // (e.g. `runConversationTurn`) can't race ahead and flip the
    // voice state to idle — which would stomp the pointing sprite
    // pose and snap the bird back into flight before the user has
    // had time to register the moment.
    await this.awaitHoldCompletion(callbacks.onHoldComplete);
    this.stopHoldTracker();
  }

  private startHoldTracker(
    refreshPosition: () => { viewportX: number; viewportY: number } | null,
  ): void {
    this.stopHoldTracker();
    // Drive tracking from `requestAnimationFrame`, not `setInterval`.
    // rAF callbacks fire synchronously BEFORE each browser paint, so the
    // bird's transform update lands in the same frame as the scrolled
    // content — without it the bird visibly lags one paint behind the
    // page during inertial scrolls.
    const step = () => {
      const next = refreshPosition();
      if (next) {
        this.movementController.setPointingPosition(next);
      }
      this.holdTrackerRafId = requestAnimationFrame(step);
    };
    this.holdTrackerRafId = requestAnimationFrame(step);
  }

  private stopHoldTracker(): void {
    if (this.holdTrackerRafId !== null) {
      cancelAnimationFrame(this.holdTrackerRafId);
      this.holdTrackerRafId = null;
    }
  }

  private awaitHoldCompletion(onHoldComplete?: () => void): Promise<void> {
    if (this.activePointingTimerId !== null) {
      window.clearTimeout(this.activePointingTimerId);
    }
    return new Promise((resolve) => {
      this.activePointingTimerId = window.setTimeout(() => {
        this.activePointingTimerId = null;
        if (onHoldComplete) {
          onHoldComplete();
        } else {
          this.movementController.returnToFollow();
        }
        resolve();
      }, POINTING_HOLD_SECONDS * 1000);
    });
  }

  destroy(): void {
    if (this.activePointingTimerId !== null) {
      window.clearTimeout(this.activePointingTimerId);
      this.activePointingTimerId = null;
    }
    this.stopHoldTracker();
  }
}
